import { newSaveRequestKey } from '../utils/saveSafety';
import React, { useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
  alpha,
  useMediaQuery,
} from '@mui/material';
import Grid from '@mui/material/GridLegacy';
import { useTheme } from '@mui/material/styles';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import EmailIcon from '@mui/icons-material/Email';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import HomeIcon from '@mui/icons-material/Home';
import LanguageIcon from '@mui/icons-material/Language';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import NotesIcon from '@mui/icons-material/Notes';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PhoneIcon from '@mui/icons-material/Phone';
import PublicIcon from '@mui/icons-material/Public';
import ReceiptIcon from '@mui/icons-material/Receipt';

const EMPTY_FORM = {
  company_name: '',
  contact_person: '',
  email: '',
  phone: '',
  status: 'active',
  address: '',
  city: '',
  state: '',
  country: '',
  postal_code: '',
  website: '',
  tax_id: '',
  notes: '',
};

const STATUS_OPTIONS = [
  { value: 'active', label: 'Active', color: 'success' },
  { value: 'inactive', label: 'Inactive', color: 'error' },
  { value: 'prospect', label: 'Prospect', color: 'warning' }
];

const ClientFormDialog = ({ open, onClose, client, onSave }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [formData, setFormData] = useState(EMPTY_FORM);
  const [formErrors, setFormErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!open) return;
    setFormData(client ? { ...EMPTY_FORM, ...client } : { ...EMPTY_FORM, client_request_id: newSaveRequestKey() });
    setFormErrors({});
    setSubmitError('');
  }, [client, open]);

  const validateForm = () => {
    const errors = {};

    if (!String(formData.company_name || '').trim()) {
      errors.company_name = 'Company name is required';
    }

    if (!String(formData.email || '').trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Invalid email';
    }

    if (formData.phone && !/^[\d\s()+-]+$/.test(formData.phone)) {
      errors.phone = 'Invalid phone number';
    }

    if (formData.website && !/^https?:\/\/.+/.test(formData.website)) {
      errors.website = 'Invalid URL (must start with http:// or https://)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (field) => (event) => {
    const value = event.target.value;
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors((prev) => ({ ...prev, [field]: '' }));
    }
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSaving(true);
    setSubmitError('');
    try {
      await onSave(formData);
      onClose();
    } catch (error) {
      setSubmitError(error.response?.data?.message || error.message || 'No se pudo confirmar el guardado. Puedes reintentar conservando tus datos.');
      console.error('Error saving client:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey && event.target.tagName !== 'TEXTAREA') {
      event.preventDefault();
      handleSubmit();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
      PaperProps={{
        sx: {
          borderRadius: isMobile ? 0 : 3,
          overflow: 'hidden',
          ...(isMobile && {
            m: 0,
            width: '100%',
            maxWidth: '100%',
            height: '100dvh',
            maxHeight: '100dvh'
          }),
          border: '1px solid',
          borderColor: 'divider',
          boxShadow: `0 26px 56px rgba(0,0,0,0.18)`
        }
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          px: { xs: 2, sm: 3 },
          pt: isMobile ? 'max(16px, env(safe-area-inset-top, 0px))' : 2.5,
          pb: 1.75,
          flexShrink: 0,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backgroundImage: `linear-gradient(135deg, rgba(25,118,210,0.12) 0%, rgba(25,118,210,0.03) 100%)`
        }}
      >
        <Avatar sx={{ bgcolor: 'primary.main', boxShadow: '0 10px 24px rgba(25,118,210,0.22)', flexShrink: 0 }}>
          {client ? <EditIcon /> : <PersonAddIcon />}
        </Avatar>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            {client ? 'Edit Client' : 'New Client'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Start with company, contact and status. The rest can be completed after.
          </Typography>
        </Box>
      </DialogTitle>

      <DialogContent sx={{
        px: { xs: 2, sm: 3 },
        pt: { xs: 2, sm: 3 },
        bgcolor: alpha(theme.palette.primary.main, 0.015),
        WebkitOverflowScrolling: 'touch',
        overscrollBehavior: 'contain'
      }}>
        <Stack spacing={2.5}>
          {submitError && <Alert severity="error">{submitError}</Alert>}
          <Alert severity="info" variant="outlined">
            Quick flow: capture the company, main contact, email and status first. Address, notes and tax info stay in the additional section.
          </Alert>

          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
              Basic setup
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={7}>
                <TextField
                  label="Company Name"
                  value={formData.company_name}
                  onChange={handleChange('company_name')}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  required
                  autoFocus
                  error={Boolean(formErrors.company_name)}
                  helperText={formErrors.company_name || 'Main name used across projects and documents'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <BusinessIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={5}>
                <TextField
                  select
                  label="Status"
                  value={formData.status}
                  onChange={handleChange('status')}
                  fullWidth
                >
                  {STATUS_OPTIONS.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      <Chip label={option.label} color={option.color} size="small" sx={{ fontWeight: 700 }} />
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Contact Person"
                  value={formData.contact_person}
                  onChange={handleChange('contact_person')}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  helperText="Optional but recommended"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AccountCircleIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  label="Phone"
                  value={formData.phone}
                  onChange={handleChange('phone')}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  error={Boolean(formErrors.phone)}
                  helperText={formErrors.phone || 'Optional'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <PhoneIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange('email')}
                  onKeyDown={handleKeyDown}
                  fullWidth
                  required
                  error={Boolean(formErrors.email)}
                  helperText={formErrors.email || 'Primary email for communication'}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <EmailIcon color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
            </Grid>
          </Box>

          <Accordion
            disableGutters
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:before': { display: 'none' }
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box>
                <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                  Additional details
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Address, website, RFC / Tax ID and internal notes.
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    label="Address"
                    value={formData.address}
                    onChange={handleChange('address')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    multiline
                    minRows={2}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <HomeIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="City"
                    value={formData.city}
                    onChange={handleChange('city')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOnIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="State / Province"
                    value={formData.state}
                    onChange={handleChange('state')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Country"
                    value={formData.country}
                    onChange={handleChange('country')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <PublicIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="Postal Code"
                    value={formData.postal_code}
                    onChange={handleChange('postal_code')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Website"
                    value={formData.website}
                    onChange={handleChange('website')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    error={Boolean(formErrors.website)}
                    helperText={formErrors.website || 'Optional (e.g. https://example.com)'}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LanguageIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    label="RFC / Tax ID"
                    value={formData.tax_id}
                    onChange={handleChange('tax_id')}
                    onKeyDown={handleKeyDown}
                    fullWidth
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <ReceiptIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Notes"
                    value={formData.notes}
                    onChange={handleChange('notes')}
                    fullWidth
                    multiline
                    minRows={3}
                    helperText="Optional - internal notes about the client"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start" sx={{ alignSelf: 'flex-start', mt: 1.5 }}>
                          <NotesIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
        </Stack>
      </DialogContent>

      <DialogActions sx={{
        px: { xs: 2, sm: 2.5 },
        pt: 1.5,
        pb: isMobile ? 'max(12px, env(safe-area-inset-bottom, 0px))' : 2.5,
        gap: 1,
        borderTop: '1px solid',
        borderColor: 'divider',
        flexShrink: 0,
        '& .MuiButton-root': { minHeight: 44 },
        '& .MuiButton-root + .MuiButton-root': { ml: 0 }
      }}>
        <Button onClick={onClose} disabled={saving} sx={{ flex: { xs: 1, sm: 'initial' } }}>
          Cancel
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={saving}
          startIcon={<CheckCircleIcon />}
          sx={{ px: 3, fontWeight: 700, flex: { xs: 1.5, sm: 'initial' } }}
        >
          {saving ? 'Saving...' : (client ? 'Update Client' : 'Create Client')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ClientFormDialog;
